import { Dayjs } from "dayjs";
import { formatBuddhistMonth, formatBuddhistShortDate } from "../../utils/date";
import { API_DATE_FORMAT } from "../SpecialOrder/data";
import { TaxReportKind } from "./constants";
import { DocumentDraft } from "./documentDraft";
import { ExpressTotals } from "./totals";

/**
 * The line a document puts on the ภาษีซื้อ / ภาษีขาย report, as the Express tab shows it. It is
 * never keyed: a purchase reports the supplier's tax invoice (its bill number, date and the
 * month it is filed in); other income reports the document itself.
 */
export interface TaxReportRow {
  /** ยื่นรวมในงวด, MM/YY Buddhist era. */
  period: string;
  /** ใบกำกับภาษี วันที่, DD/MM/YY Buddhist era. */
  date: string;
  /** ใบกำกับภาษี เลขที่ */
  docNo: string;
  departmentCode: string;
  title: string;
  name: string;
  taxId: string;
  taxBranchNo: string;
  baseAmount: number;
  vatAmount: number;
}

export interface TaxReportSource {
  kind: TaxReportKind;
  draft: DocumentDraft;
  totals: ExpressTotals;
  /** What only a saved document knows: its number and its department. */
  docNo: string;
  departmentCode: string;
}

const dayText = (date: Dayjs | null) =>
  date?.isValid() ? formatBuddhistShortDate(date.format(API_DATE_FORMAT)) : "";

const monthText = (date: Dayjs | null) =>
  date?.isValid() ? formatBuddhistMonth(date.format("YYYY-MM")) : "";

/** The document's tax line; none while it is priced without VAT, since there is no tax to report. */
export const taxReportRow = ({
  kind,
  draft,
  totals,
  docNo,
  departmentCode,
}: TaxReportSource): TaxReportRow | null => {
  if (draft.priceType === 0) return null;
  const isPurchase = kind === "PURCHASE";
  const { party } = draft;
  return {
    period: monthText(isPurchase ? draft.taxPeriod : draft.docDate),
    date: dayText(isPurchase ? draft.supplierBillDate : draft.docDate),
    docNo: isPurchase ? draft.supplierBillNo.trim() : docNo,
    departmentCode,
    title: party?.title ?? "",
    name: party?.name ?? "",
    taxId: party?.taxId ?? "",
    taxBranchNo: party?.taxBranchNo === null || party?.taxBranchNo === undefined ? "" : String(party.taxBranchNo),
    baseAmount: totals.baseAmount,
    vatAmount: totals.vatAmount,
  };
};
